import { useState, useEffect } from 'react';
import { usersAPI, propertiesAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, UserCog, Building2, Trash2, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';

const ROLES = ['standard', 'vendor', 'admin', 'superadmin'];
const ROLE_COLORS = {
  superadmin: 'bg-red-100 text-red-800 border-red-200',
  admin: 'bg-blue-100 text-blue-800 border-blue-200',
  vendor: 'bg-amber-100 text-amber-800 border-amber-200',
  standard: 'bg-gray-100 text-gray-800 border-gray-200',
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState(null);
  const [selectedProps, setSelectedProps] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [usersRes, propsRes] = await Promise.all([
        usersAPI.list(),
        propertiesAPI.list(),
      ]);
      setUsers(usersRes.data);
      setProperties(propsRes.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      toast.success('Role updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleToggle = async (userId) => {
    try {
      await usersAPI.toggle(userId);
      toast.success('User status toggled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersAPI.remove(userId);
      toast.success('User deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openAssignDialog = (user) => {
    setAssignDialog(user);
    setSelectedProps(user.assigned_properties || []);
  };

  const handleAssignProperties = async () => {
    if (!assignDialog) return;
    setSaving(true);
    try {
      await usersAPI.assignProperties(assignDialog.id, selectedProps);
      toast.success('Properties assigned');
      setAssignDialog(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to assign properties');
    } finally {
      setSaving(false);
    }
  };

  const toggleProp = (propId) => {
    setSelectedProps((prev) =>
      prev.includes(propId) ? prev.filter((p) => p !== propId) : [...prev, propId]
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="users-management-page">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-semibold">User Management</h1>
          </div>
          <p className="text-muted-foreground">
            Manage roles, permissions, and property assignments for all users.
          </p>
        </div>

        <div className="border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Properties</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="w-[180px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <select
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        disabled={isSelf}
                        className={`text-xs px-2 py-1 rounded-md border font-medium ${ROLE_COLORS[u.role] || ''}`}
                        data-testid={`role-select-${u.id}`}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(u.assigned_properties || []).length > 0 ? (
                          (u.assigned_properties || []).map((pId) => {
                            const prop = properties.find((p) => p.id === pId);
                            return (
                              <Badge key={pId} variant="outline" className="text-xs">
                                {prop?.name || pId.slice(0, 8)}
                              </Badge>
                            );
                          })
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {['admin', 'superadmin'].includes(u.role) ? 'All' : 'None'}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.is_active !== false ? 'default' : 'secondary'}>
                        {u.is_active !== false ? 'Active' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAssignDialog(u)}
                          title="Assign Properties"
                          data-testid={`assign-props-${u.id}`}
                        >
                          <Building2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggle(u.id)}
                          disabled={isSelf}
                          title={u.is_active !== false ? 'Disable' : 'Enable'}
                          data-testid={`toggle-user-${u.id}`}
                        >
                          {u.is_active !== false ? (
                            <ToggleRight className="h-4 w-4 text-green-600" />
                          ) : (
                            <ToggleLeft className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(u.id)}
                          disabled={isSelf}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Assign Properties Dialog */}
        <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Properties to {assignDialog?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {properties.map((prop) => (
                <label
                  key={prop.id}
                  className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedProps.includes(prop.id)}
                    onChange={() => toggleProp(prop.id)}
                    className="rounded border-input"
                  />
                  <div>
                    <p className="font-medium text-sm">{prop.name}</p>
                    <p className="text-xs text-muted-foreground">{prop.city}, {prop.country}</p>
                  </div>
                </label>
              ))}
              {properties.length === 0 && (
                <p className="text-muted-foreground text-center py-4">No properties available</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setAssignDialog(null)}>Cancel</Button>
              <Button onClick={handleAssignProperties} disabled={saving} data-testid="save-properties-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
